import {
  AccountAlreadyExistsError,
  AccountAlreadyExistsSmsError,
  ChurchRecordNumberAlreadyInUseError,
  ChurchRecordNumberInvalidError,
  ChurchRecordNumberParentInvalidError,
  EmailMaxAccountsError,
  InvalidCharacterEmailError,
  InvalidCharacterFirstNamesError,
  InvalidCharacterLastNamesError,
  InvalidCharacterUsernameError,
  InvalidEmailError,
  InvalidEmailReportedError,
  InvalidFieldError,
  InvalidLastNamesError,
  InvalidSmsNumberError,
  InvalidUsernameError,
  MobileNumberAlreadyInUseError,
  MobileNumberInvalidError,
  ParentalConsentRequiredError,
  UsernameAlreadyInUseError,
  InvalidFirstNamesError,
} from '../errors'
import noticeError from './noticeError'

// See validation documentation and codes at the following links:
// - https://github.com/fs-eng/identity-cis/blob/master/cis-internal-only-clients/cis-common-client/src/main/java/org/familysearch/cis/client/CISWarningCodes.java
// - https://icseng.atlassian.net/wiki/display/Product/Validation+Rules+for+CIS+accounts
// - https://github.com/fs-eng/identity-cis/blob/master/cis-core/src/main/java/org/familysearch/cis/validation/BaseValidator.java
// Include all codes relevant for profile text fields even if the errors should be handled by client validation
const cisWarningCodeToError = {
  136: InvalidFirstNamesError, // contains MRN
  137: InvalidFirstNamesError, // required
  138: InvalidFirstNamesError, // too long
  139: InvalidCharacterFirstNamesError,
  140: InvalidLastNamesError, // contains MRN
  141: InvalidLastNamesError, // required
  142: InvalidLastNamesError, // too long
  143: InvalidCharacterLastNamesError, // invalid characters
  144: InvalidCharacterUsernameError,
  145: InvalidUsernameError, // required
  147: UsernameAlreadyInUseError,
  148: InvalidUsernameError,
  149: InvalidUsernameError, // contains mrn
  151: InvalidUsernameError, // required
  152: InvalidUsernameError, // required
  154: InvalidUsernameError, // too short
  155: InvalidCharacterUsernameError,
  162: InvalidEmailError, // domain invalid
  163: InvalidEmailError,
  164: InvalidEmailReportedError, // required
  165: EmailMaxAccountsError, // shared with too many accounts
  166: InvalidEmailReportedError, // too long
  169: InvalidCharacterEmailError,
  172: InvalidSmsNumberError, // required
  173: MobileNumberInvalidError,
  174: InvalidSmsNumberError, // invalid characters
  175: InvalidSmsNumberError, // too short
  176: InvalidSmsNumberError, // too long
  177: MobileNumberAlreadyInUseError,
  194: ChurchRecordNumberInvalidError, // birthdate invalid
  196: ChurchRecordNumberAlreadyInUseError,
  197: ChurchRecordNumberInvalidError,
  199: ChurchRecordNumberInvalidError, // illegal characters
  200: AccountAlreadyExistsError, // email with full name already in use
  228: MobileNumberInvalidError, // from parental consent
  238: ParentalConsentRequiredError,
  241: InvalidUsernameError,
  245: MobileNumberAlreadyInUseError,
  257: InvalidEmailError, // from parental consent
  258: ChurchRecordNumberParentInvalidError,
  262: AccountAlreadyExistsSmsError,
}

export default function throwCisError(error) {
  const warningCode = findWarningCode(error)
  if (warningCode) {
    const MappedError = cisWarningCodeToError[warningCode]
    if (MappedError) {
      const mappedError = new MappedError()
      if (mappedError instanceof InvalidFieldError) {
        const message = `Invalid ${mappedError.fieldName}: ${warningCode}`
        noticeError(new Error(message), [message])
      }
      throw mappedError
    }
  }
  throw error
}

const digits = /\d+/g

function findWarningCode(error) {
  let codes = (error?.response?.headers?.warning ?? '').match(digits)
  if (!codes) {
    return ''
  }
  // Convert to unique array of codes as sometimes redundant codes are returned
  codes = [...new Set(codes)]
  // Take the first code
  let [code] = codes
  if (codes.length > 1) {
    // handle other codes before parental consent code if possible (IDENT-9001)
    if (code === '238') {
      code = cisWarningCodeToError[codes[1]] ? codes[1] : code
    } else {
      // currently UI does not handle multiple codes in most instances so let's log these
      // to find out if it could be useful to handle
      const message = String(codes) // e.g. '154,153'
      noticeError(new Error(message), [message])
    }
  }
  return code
}
